import React, { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import WompiButton from "@/components/WompiButton";
import NequiButton from "@/components/NequiButton";
import { IS_NEQUI_ENABLED } from "@/lib/config/featureFlags";
import { Check, Star, Zap, Shield } from "lucide-react";
import type { PlanWithFeatures } from "@/lib/services/plans";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
interface PlanSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  plans: PlanWithFeatures[];
}

const YEARLY_DISCOUNT = 0.2; // 20% discount for yearly billing

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const PlanSelectionModal = ({
  isOpen,
  onClose,
  plans,
}: PlanSelectionModalProps) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState<PlanWithFeatures | null>(
    null
  );
  const [isYearly, setIsYearly] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isWarRoomPromo =
    typeof window !== "undefined" &&
    (localStorage.getItem("mia_promo_code") === "WARROOM20" ||
      localStorage.getItem("mia_referral_code") === "WARROOM20");

  const calculatePrice = (monthlyPrice: number) => {
    if (isYearly) {
      const yearlyPrice = monthlyPrice * 12;
      const discountedPrice = yearlyPrice * (1 - YEARLY_DISCOUNT);
      return discountedPrice;
    }
    if (isWarRoomPromo) {
      return Math.round(monthlyPrice * 0.8);
    }
    return monthlyPrice;
  };

  const handleSuccess = () => {
    toast({
      title: "Éxito",
      description: "Tu suscripción se ha procesado correctamente",
    });
    onClose();
  };

  const handleError = (_error: unknown) => {
    toast({
      title: "Error",
      description: "Error al procesar el pago. Por favor intenta de nuevo.",
      variant: "destructive",
    });
    setIsSubmitting(false);
  };

  if (!user) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-xl md:text-2xl text-legal-navy">
              Inicia Sesión
            </DialogTitle>
            <DialogDescription>
              Por favor inicia sesión para suscribirte a un plan
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl gap-0 p-0 overflow-y-auto max-h-[92vh] custom-scrollbar border-none shadow-2xl rounded-3xl">
        <div className="bg-gradient-to-r from-legal-navy to-slate-900 py-5 px-6 md:py-6 md:px-8 text-white relative">
          <div className="relative z-10">
            <DialogTitle className="text-2xl md:text-3xl font-bold mb-1.5">
              Selecciona tu Plan
            </DialogTitle>
            <DialogDescription className="text-slate-300 text-sm">
              Obtén acceso a la potencia de la IA legal enterprise. Elige el plan que mejor se adapte a tu firma.
            </DialogDescription>
          </div>
          <div className="absolute top-0 right-0 p-6 md:p-8 opacity-10">
            <Shield className="h-28 w-28 md:h-32 md:w-32" />
          </div>
        </div>

        <div className="p-5 md:p-8 bg-white">
          <div className="flex flex-col items-center mb-5 md:mb-8">
            <div className="inline-flex items-center gap-4 bg-slate-50 p-2 rounded-2xl border border-slate-100 shadow-sm">
              <Label
                htmlFor="billing-toggle"
                className={cn(
                  "text-sm font-semibold transition-colors px-3",
                  !isYearly ? "text-legal-blue" : "text-slate-400"
                )}
              >
                Pago Mensual
              </Label>
              <Switch
                id="billing-toggle"
                checked={isYearly}
                onCheckedChange={setIsYearly}
                className="data-[state=checked]:bg-legal-blue"
              />
              <div className="flex items-center gap-2 px-3">
                <Label
                  htmlFor="billing-toggle"
                  className={cn(
                    "text-sm font-semibold transition-colors",
                    isYearly ? "text-legal-blue" : "text-slate-400"
                  )}
                >
                  Pago Anual
                </Label>
                <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-none font-bold text-[10px] px-2 py-0.5 animate-pulse">
                  AHORRA 20%
                </Badge>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
            {plans.map((plan) => {
              const isPopular = plan.name.toLowerCase() === "profesional";
              const isEnterprise = plan.name.toLowerCase() === "empresarial";

              return (
                <div
                  key={plan.id}
                  className={cn(
                    "relative flex flex-col rounded-3xl border transition-all duration-300 group hover:shadow-2xl",
                    selectedPlan?.id === plan.id
                      ? "border-legal-blue ring-1 ring-legal-blue/20 bg-blue-50/30"
                      : "border-slate-200 hover:border-legal-blue/50 bg-white",
                    isPopular && "scale-105 z-10 shadow-xl border-legal-blue/30"
                  )}
                  onClick={() => setSelectedPlan(plan)}>

                  {isPopular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-legal-blue to-indigo-600 text-white text-[10px] font-bold px-4 py-1.5 rounded-full shadow-lg flex items-center gap-1.5 whitespace-nowrap">
                      <Star className="h-3 w-3 fill-current" />
                      MÁS POPULAR
                    </div>
                  )}

                  <div className="p-4 md:p-6 flex flex-col flex-1">
                    <div className="mb-4 md:mb-6">
                      <div className="flex items-center justify-between mb-1.5 md:mb-2">
                        <h3 className="text-xl font-bold text-legal-navy">
                          {plan.name}
                        </h3>
                        {isPopular ? <Zap className="h-5 w-5 text-amber-500" /> : <div className="h-5 w-5" />}
                      </div>

                      <div className="flex items-baseline gap-1 mb-1">
                        <span className="text-2xl md:text-3xl font-black text-legal-blue">
                          {formatCurrency(calculatePrice(plan.price_per_month))}
                        </span>
                        <span className="text-sm font-medium text-slate-400">
                          {isYearly ? "COP /año" : "COP /mes"}
                        </span>
                      </div>

                      {isYearly ? (
                        <p className="text-xs font-bold text-emerald-600">
                          {formatCurrency(calculatePrice(plan.price_per_month) / 12)} /mes facturado anual
                        </p>
                      ) : isWarRoomPromo ? (
                        <p className="text-xs font-bold text-amber-600 dark:text-amber-400 flex items-center gap-1">
                          <span>🏷️ 20% OFF Cortesía War Room (1er mes)</span>
                        </p>
                      ) : (
                        <p className="text-xs text-slate-400">
                          Total anual: {formatCurrency(plan.price_per_month * 12)}
                        </p>
                      )}
                    </div>

                    <div className="flex-1">
                      <p className="text-xs font-bold text-slate-900 uppercase tracking-widest mb-2">
                        Incluye:
                      </p>
                      <div className={cn(
                        "space-y-2.5 custom-scrollbar overflow-y-auto pr-2",
                        isEnterprise ? "max-h-[200px]" : "max-h-[160px]"
                      )}>
                        <ul className="space-y-3">
                          {plan.features.map((feature) => (
                            <li
                              key={feature.id}
                              className="flex items-start gap-3 text-[13px] leading-relaxed text-slate-600">
                              <div className="mt-0.5 rounded-full bg-emerald-100 p-0.5 flex-shrink-0">
                                <Check className="h-3 w-3 text-emerald-600 stroke-[3]" />
                              </div>
                              <span>{feature.description}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <div className="mt-8">
                      {selectedPlan?.id === plan.id ? (
                        IS_NEQUI_ENABLED ? (
                          <NequiButton
                            amount={calculatePrice(plan.price_per_month)}
                            planId={plan.id}
                            planName={plan.name}
                            buttonText={`Pagar con Nequi (${plan.name})`}
                            className="h-11"
                            onSuccess={handleSuccess}
                          />
                        ) : (
                          <WompiButton
                            amount={calculatePrice(plan.price_per_month)}
                            planId={plan.id}
                            planName={plan.name}
                            buttonText={`Seleccionar ${plan.name}`}
                            className="h-11"
                            onBeforeOpen={onClose}
                            onSuccess={handleSuccess}
                            onError={handleError}
                          />
                        )
                      ) : (
                        <Button
                          variant={isPopular ? "default" : "outline"}
                          className={cn(
                            "w-full rounded-xl font-bold h-11 transition-all",
                            isPopular && "bg-legal-blue hover:bg-indigo-700 shadow-md shadow-legal-blue/20"
                          )}
                        >
                          Seleccionar {plan.name}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PlanSelectionModal;
